// Angular imports
import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  Input,
  Output,
  EventEmitter
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

// 3rd party imports
import {
  DxProgressBarComponent,
  DxValidationGroupComponent
} from 'devextreme-angular';
import { TextBoxType } from 'devextreme/ui/text_box';

// Component imports
import {
  IconConstants,
  CCPasswordHelperService
} from '@therealmanage/ciranet-ui-common';
import { RecaptchaBaseComponent } from '../../_base/recaptcha-base.component';

// Service imports
import { EnvironmentConfigService } from '../../_services/environment-config.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { UserService } from '../../_services/data-services/user.service';

// Model imports
import { CreateUserParameterDto } from '../../_models/create-user-parameter-dto';
import { RegistrationEditorResultDto } from '../../_models/registration/registration-editor-result-dto';
import { ValidateRegistrationCredentialsDto } from '../../_models/validate-registration-credentials-dto';
import { ValidateRegistrationCredentialsResultDto } from '../../_models/validate-registration-credentials-result-dto';

// Constant imports
import { ValidationFormatConstants } from '../../_constants/validation-format-constants';

// CC-Common imports
import { Utils, RxjsUtils } from '@therealmanage/ciranet-ui-common';
@Component({
  selector: 'app-register-details',
  templateUrl: './register-details.component.html',
  styleUrls: ['./register-details.component.scss'],
  standalone: false
})
export class RegisterDetailsComponent
  extends RecaptchaBaseComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild('registerDetailsValidationGroup', { static: false })
  registerDetailsValidationGroup: DxValidationGroupComponent;
  @ViewChild('passStrengthSuccess', { static: false })
  passStrengthSuccess: DxProgressBarComponent;

  @Input() userInfo: CreateUserParameterDto;
  @Input() registrationEditor: RegistrationEditorResultDto;
  @Input() isEmbedded = false;
  @Output() closePanel = new EventEmitter<void>();
  @Output() onBack = new EventEmitter<void>();
  @Output() onRegistrationUpdate = new EventEmitter<any>();

  isSmallScreen = false;
  loadingVisible = false;
  passwordMode: TextBoxType;
  passwordShowButton: object;
  passwordHideButton: object;
  passwordStrength = 0;
  passwordRules: any[] = [];
  emailPattern: RegExp = ValidationFormatConstants.emailPattern;
  iconCircleCheck = IconConstants.CIRCLE_CHECK;
  showErrorMessage = false;
  errorMessage = '';
  showGoogleAuth = true;
  showAccountAlreadyRegistered = false;
  private subscriptions = new Subscription();

  constructor(
    private router: Router,
    private screenService: ScreenService,
    private passwordHelper: CCPasswordHelperService,
    private userService: UserService,
    protected override errorService: ServerErrorService,
    protected override envConfig: EnvironmentConfigService
  ) {
    super(errorService, envConfig);
  }

  ngOnInit(): void {
    if (!this.registrationEditor) {
      console.error('registrationEditor is required');
      this.onBack.emit(); // Navigate back gracefully
      return;
    }

    // Initialize userInfo if not provided
    if (!this.userInfo) {
      this.userInfo = new CreateUserParameterDto();
    }

    // Ensure password property exists
    if (!this.userInfo.password) {
      this.userInfo.password = '';
    }

    this.initializePasswordRules();
    this.passwordMode = 'password';
    this.passwordShowButton = {
      icon: 'fa-solid fa-eye',
      type: 'default',
      stylingMode: 'text',
      hint: 'Show Password',
      onClick: () => {
        this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
      }
    };
    this.passwordHideButton = {
      icon: 'fa-solid fa-eye-slash',
      type: 'default',
      stylingMode: 'text',
      hint: 'Hide Password',
      onClick: () => {
        this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
      }
    };
    this.updateScreenSize();
    this.subscriptions.add(
      RxjsUtils.safeSubscribe(this.screenService.changed, () =>
        this.updateScreenSize()
      )
    );

    // Validate password rules if present (e.g., when navigating back)
    if (this.userInfo.password) {
      this.validatePasswordRules(this.userInfo.password);
    }
  }

  ngAfterViewInit(): void {
    // Update progress bar after view is initialized (when navigating back with existing password)
    if (this.userInfo.password && this.passStrengthSuccess) {
      this.updatePasswordStrength(this.userInfo.password);
    }
  }

  private validatePasswordRules(password: string): void {
    this.passwordStrength = this.passwordHelper.getPasswordStrength(password);
    this.passwordRules.forEach((rule) => {
      rule.isValid = rule.check(password);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  btnCompleteRegistrationClick(): void {
    // Clear any previous error messages
    this.showErrorMessage = false;
    this.errorMessage = '';

    if (
      this.registerDetailsValidationGroup.instance.validate().isValid == false
    ) {
      return;
    }

    if (this.userInfo.password.length < 8) {
      this.showErrorMessage = true;
      this.errorMessage = 'Password must have at least 8 characters.';
      return;
    }

    if (
      Utils.isFalse(
        this.passwordHelper.hasSpecialCharacter(this.userInfo.password)
      )
    ) {
      this.showErrorMessage = true;
      this.errorMessage =
        'Your password must contain at least one special character';
      return;
    }

    if (Utils.isFalse(this.passwordHelper.hasNumeric(this.userInfo.password))) {
      this.showErrorMessage = true;
      this.errorMessage = 'Your password must contain at least one number.';
      return;
    }

    if (this.passwordStrength < 5) {
      this.showErrorMessage = true;
      this.errorMessage =
        'Your password must include at least one lowercase letter and one uppercase letter.';
      return;
    }

    if (!this.isValidRecaptcha()) {
      this.showErrorMessage = true;
      this.errorMessage = 'Please complete the reCAPTCHA verification.';
      return;
    }

    // Assign reCAPTCHA token to userInfo for use in later registration steps
    this.userInfo.recaptchaToken = this.recaptchaToken;

    // Validate credentials before proceeding
    this.validateCredentialsAndProceed();
  }

  private validateCredentialsAndProceed(): void {
    const credentials = new ValidateRegistrationCredentialsDto();
    credentials.email = this.userInfo.email;
    credentials.password = this.userInfo.password;

    this.loadingVisible = true;
    this.subscriptions.add(
      RxjsUtils.safeSubscribe(
        this.userService.validateRegistrationCredentials(credentials),
        (validationResult: ValidateRegistrationCredentialsResultDto) => {
          this.loadingVisible = false;
          if (!validationResult.isValid) {
            this.handleValidationFailure(validationResult);
            return;
          }
          this.updateRegistration();
        },
        (error) => {
          this.loadingVisible = false;
          this.processApiError(error);
        }
      )
    );
  }

  private handleValidationFailure(
    validationResult: ValidateRegistrationCredentialsResultDto
  ): void {
    // Check if email is already registered - show dedicated screen
    if (validationResult.hasEmailError()) {
      this.showAccountAlreadyRegistered = true;
      return;
    }

    // For password errors, show inline error message
    const passwordError = validationResult.getPasswordError();
    if (passwordError) {
      this.showErrorMessage = true;
      this.errorMessage = passwordError;
    }
  }

  btnForgotPassword(): void {
    this.router.navigate(['user', 'forgotpassword']);
  }

  btnForgotUsername(): void {
    this.router.navigate(['user', 'forgotusername']);
  }

  private updateRegistration(): void {
    this.onRegistrationUpdate.emit({
      userInfo: this.userInfo,
      registrationEditor: this.registrationEditor
    });
  }

  btnBackClick(): void {
    this.onBack.emit();
  }

  btnSignInClick(event: any): void {
    if (this.isEmbedded) {
      this.closePanel.emit();
    } else {
      this.router.navigate(['user', 'login']);
    }
  }

  btnGoogleSignupClick(): void {
    // Validate reCAPTCHA before proceeding with Google signup
    if (!this.isValidRecaptcha()) {
      this.messageBox.showErrorMessage(
        'Error',
        'Please complete the reCAPTCHA verification'
      );
      return;
    }

    // Store registration data for use after Google callback
    // Include contactInfo to preserve owner selection
    const googleRegisterData = {
      customerID: this.userInfo.customerID,
      checkDigit: this.userInfo.checkDigit,
      recaptchaToken: this.recaptchaToken,
      contactInfo: this.userInfo.contactInfo,
      origin: 'login' // Track where the user came from (login page registration panel)
    };
    sessionStorage.setItem(
      'google_register_data',
      JSON.stringify(googleRegisterData)
    );

    // Redirect to Google OAuth
    const spaOrigin = window.location.origin;
    const googleRegisterUrl = `${this.envConfig.ccAuthenticationApiUrl}google-register-challenge?portalType=RP&spaOrigin=${encodeURIComponent(spaOrigin)}`;
    window.location.href = googleRegisterUrl;
  }

  runPasswordValidation(e: any): void {
    const value = e.event.target.value;
    this.updatePasswordStrength(value);
    this.passwordRules.forEach((rule) => {
      rule.isValid = rule.check(value);
    });
  }

  updatePasswordStrength(passValue: string): void {
    this.passwordStrength = this.passwordHelper.getPasswordStrength(passValue);
    if (this.passStrengthSuccess) {
      this.passStrengthSuccess.value = this.passwordStrength;
    }
  }

  emailComparison = () => {
    return this.userInfo.email;
  };

  passwordComparison = () => {
    return this.userInfo.password;
  };

  getPasswordStrengthText(value: number): string {
    return Math.round(value * 100) + '%';
  }

  private initializePasswordRules(): void {
    this.passwordRules = [
      {
        label: '8 characters',
        isValid: false,
        check: (value: string) => value.length >= 8
      },
      {
        label: '1 lowercase (a-z) character',
        isValid: false,
        check: (value: string) => this.passwordHelper.hasLowerCase(value)
      },
      {
        label: '1 uppercase (A-Z) character',
        isValid: false,
        check: (value: string) => this.passwordHelper.hasUpperCase(value)
      },
      {
        label: '1 number (0-9) character',
        isValid: false,
        check: (value: string) => this.passwordHelper.hasNumeric(value)
      },
      {
        label: `Special characters (!@#$%^&*-+()_={}[]|\\:;"'<>,.?/~\`)`,
        isValid: false,
        check: (value: string) => this.passwordHelper.hasSpecialCharacter(value)
      }
    ];
  }

  private updateScreenSize(): void {
    this.isSmallScreen = this.screenService.isSmallScreen();
  }
}
